"use client";

import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";

import { env } from "@/env";
import { generateJobUrl } from "@/helpers/url";
import { useAsyncRouter } from "@/hooks/useAsyncRouter";
import { AllowedLanguages } from "@/types/generic";
import { CharlotteChatContext } from "./CharlotteChatContext";
import { useCharlotteChatIframeEvents } from "./hooks/useCharlotteChatIframeEvents";
import { CharlotteChatState } from "./types";

const CharlotteChatLayer = dynamic(() => import("./CharlotteChatLayer"), {
  ssr: false,
});

const CHARLOTTE_CHAT_BASE_URL = env.NEXT_PUBLIC_CHARLOTTE_CHAT_BASE_URL;

export interface CharlotteChatProviderProps {
  children: ReactNode;
  locale: AllowedLanguages;
}

export const CharlotteChatProvider = ({
  children,
  locale,
}: CharlotteChatProviderProps) => {
  const router = useAsyncRouter();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [chatState, setChatState] = useState<CharlotteChatState>("closed");

  const iframeSrc = useMemo(() => {
    const url = new URL(`/${locale}/charlotte`, `${CHARLOTTE_CHAT_BASE_URL}`);
    return url.toString();
  }, [locale]);

  const iframeOrigin = useMemo(
    () => new URL(CHARLOTTE_CHAT_BASE_URL).origin,
    [],
  );

  const handleOpen = useCallback(() => {
    setChatState("open");
  }, []);

  const handleClose = useCallback(() => {
    setChatState("closed");
  }, []);

  const handleMinimize = useCallback(() => {
    setChatState("minimized");
  }, []);

  const handleNavigateToJob = useCallback(
    async ({ jobId, jobName }: { jobId: string; jobName: string }) => {
      await router.push(generateJobUrl(locale, jobName, jobId));
      handleMinimize();
    },
    [locale, handleMinimize, router],
  );

  useCharlotteChatIframeEvents({
    iframeOrigin,
    iframeRef,
    onMinimizeCharlotteChatEvent: handleClose,
    onNavigateToJobEvent: handleNavigateToJob,
  });

  const contextValue = useMemo(
    () => ({
      chatState,
      close: handleClose,
      iframeSrc,
      minimize: handleMinimize,
      open: handleOpen,
    }),
    [chatState, handleClose, iframeSrc, handleMinimize, handleOpen],
  );

  return (
    <CharlotteChatContext.Provider value={contextValue}>
      {children}

      <CharlotteChatLayer iframeRef={iframeRef} locale={locale} />
    </CharlotteChatContext.Provider>
  );
};
